<template>
    <div class="contents">
        <div class="container-fluid">
            <!-- Header Section -->
            <div class="row mb-4">
                <div class="col-lg-12">
                    <div class="breadcrumb-main">
                        <h2 class="text-capitalize breadcrumb-title fw-bold">Tagihan & Paket</h2>
                        <p class="text-muted mb-0">Kelola saldo dan pilih paket yang sesuai kebutuhan Anda</p>
                    </div>
                    
                    <!-- Expired Alert -->
                    <div v-if="isExpired()" class="alert alert-danger d-flex align-items-center" role="alert">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        <div>
                            <strong>Akun Anda Telah Expired!</strong>
                            <br>
                            <small>Silakan perpanjang paket untuk melanjutkan layanan</small>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="row">
                <div class="col-12">
                    <div class="text-center py-5">
                        <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;" role="status">
                        </div>
                        <p class="mt-3 text-muted">Memuat data...</p>
                    </div>
                </div>
            </div>

            <!-- Main Content -->
            <div v-else class="row">
                <!-- Balance and Current Package Section -->
                <div class="col-xxl-12 col-sm-12 mb-4">
                    <div class="row">
                        <!-- Balance Card -->
                        <div class="col-lg-6 col-md-12 mb-4">
                            <div class="card balance-card h-100">
                                <div class="card-body p-4">
                                    <div class="d-flex align-items-center mb-3">
                                        <div class="balance-icon me-3">
                                            <i class="fas fa-wallet fa-2x"></i>
                                        </div>
                                        <div>
                                            <h6 class="card-subtitle text-muted mb-1">Saldo Tersedia</h6>
                                            <h2 class="card-title text-dark mb-0 fw-bold">{{ formatRupiah(user_balance) }}</h2>
                                        </div>
                                    </div>
                                    
                                    <div class="row g-2 mt-4">
                                        <div class="col-6">
                                            <button @click="toggleModal('topup')" 
                                                    class="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center">
                                                <i class="fas fa-plus me-2"></i>
                                                Top Up
                                            </button>
                                        </div>
                                        <div class="col-6">
                                            <button @click="toggleModal('withdraw')" 
                                                    class="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center">
                                                <i class="fas fa-arrow-down me-2"></i>
                                                Withdraw
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div class="row g-2 mt-2">
                                        <div class="col-12">
                                            <router-link to="billing/transaction" class="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-center">
                                                <i class="fas fa-list me-2"></i>
                                                Riwayat Transaksi
                                            </router-link>
                                        </div>
                                    </div>

                                    <div v-if="affiliateOrderLink" class="mt-3 p-2 rounded" style="background:#f8f9fc;font-size:13px">
                                        <div class="text-muted mb-1"><i class="fas fa-link me-1"></i> Link afiliasi order publik</div>
                                        <div class="input-group input-group-sm">
                                            <input type="text" class="form-control" :value="affiliateOrderLink" readonly />
                                            <button type="button" class="btn btn-outline-primary" @click="copyAffiliateOrderLink">Salin</button>
                                        </div>
                                        <small class="text-muted d-block mt-1">Bagikan link ini — komisi mengikuti pengaturan referral di admin.</small>
                                    </div>
                                    
                                    <div class="mt-3">
                                        <button @click="toggleModal('sync');sync()" 
                                                class="btn btn-outline-info btn-sm w-100 d-flex align-items-center justify-content-center">
                                            <i class="fas fa-sync-alt me-2"></i>
                                            Sinkronisasi Billing V2
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Current Package Card -->
                        <div class="col-lg-6 col-md-12 mb-4">
                            <div class="card current-package-card h-100">
                                <div class="card-body p-4">
                                    <div class="d-flex align-items-center mb-3">
                                        <div class="package-icon me-3" style="margin-end:10px">
                                            <i class="fas fa-crown fa-2x"></i>
                                        </div>
                                        <div>
                                            <h6 class="card-subtitle text-muted mb-1">Paket Aktif</h6>
                                            <h4 class="card-title text-dark mb-0 fw-bold">{{ myPackage.name }}</h4>
                                        </div>
                                    </div>
                                    
                                    <div class="package-features">
                                        <div class="row g-2">
                                            <div class="col-6" v-if="myPackage.pesan >= 1 || myPackage.pesan == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-comment text-success" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.pesan }} {{ $t('dashboard.message') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.device >= 1 || myPackage.device == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-mobile-alt text-primary me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.device }} {{ $t('dashboard.device') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.customer_service >= 1 || myPackage.customer_service == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-headset text-info me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.customer_service }} {{ $t('dashboard.customer_service') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.campaign >= 1 || myPackage.campaign == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-bullhorn text-warning me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.campaign }} {{ $t('dashboard.campaign') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.link_rotator >= 1 || myPackage.link_rotator == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-link text-secondary me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.link_rotator }} {{ $t('dashboard.link_rotator') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.chat_widget >= 1 || myPackage.chat_widget == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-comments text-success me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.chat_widget }} {{ $t('dashboard.chat_widget') }}</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.chat_gpt >= 1 || myPackage.chat_gpt == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-robot text-purple me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.chat_gpt }} Chat GPT</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.daily_leads >= 1 || myPackage.daily_leads == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-chart-line text-info me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.daily_leads }} Daily Leads</span>
                                                </div>
                                            </div>
                                            <div class="col-6" v-if="myPackage.group_greeter >= 1 || myPackage.group_greeter == 'Unlimited'">
                                                <div class="feature-item">
                                                    <i class="fas fa-users text-primary me-2" style="margin-end:5px"></i>
                                                    <span class="feature-text">{{ myPackage.group_greeter }} Group Greeter</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Premium Features -->
                                        <div class="premium-features mt-3" v-if="myPackage.webhook_group == 1 || myPackage.autoreply_group == 1 || myPackage.webhook_premium == 1 || myPackage.advance_contact == 1 || myPackage.birthday == 1 || myPackage.follow_up == 1">
                                            <h6 class="text-muted mb-2">Fitur Premium:</h6>
                                            <div class="row g-2">
                                                <div class="col-6" v-if="myPackage.webhook_group == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Webhook Group</span>
                                                    </div>
                                                </div>
                                                <div class="col-6" v-if="myPackage.autoreply_group == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Autoreply Group</span>
                                                    </div>
                                                </div>
                                                <div class="col-6" v-if="myPackage.webhook_premium == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Webhook Premium</span>
                                                    </div>
                                                </div>
                                                <div class="col-6" v-if="myPackage.advance_contact == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Advanced Contact</span>
                                                    </div>
                                                </div>
                                                <div class="col-6" v-if="myPackage.birthday == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Birthday / Ulang Tahun</span>
                                                    </div>
                                                </div>
                                                <div class="col-6" v-if="myPackage.follow_up == 1">
                                                    <div class="feature-item premium">
                                                        <i class="fas fa-check-circle text-warning me-2" style="margin-end:5px"></i>
                                                        <span class="feature-text">Follow Up Otomatis</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="package-expiry mt-3">
                                        <div class="expiry-badge">
                                            <i class="fas fa-clock me-2" style="margin-end:5px"></i>
                                            Berlaku sampai: <span class="expiry-date">{{ myPackage.expired_at }}</span>
                                        </div>
                                    </div>
                                    
                                    <div v-if="$store.state.auth.user.level == 'user-agency' && extendURL" class="mt-3">
                                        <a :href="extendURL" target="_blank" class="btn btn-dark btn-sm w-100">
                                            <i class="fas fa-external-link-alt me-2"></i>
                                            Perpanjang Paket
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Package Sections -->
                <div class="col-12">
                    <!-- Main Packages -->
                    <div class="mb-5">
                        <div class="section-header mb-4">
                            <h3 class="fw-bold text-dark mb-2">
                                <i class="fas fa-star text-warning me-2"></i>
                                Paket Utama
                            </h3>
                            <p class="text-muted mb-0">Pilih paket yang sesuai dengan kebutuhan bisnis Anda</p>
                        </div>
                        
                        <div class="row">
                            <div v-for="(value, index) in packageListNotAddon" :key="'paket-' + index"
                                class="col-xxl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                <div class="package-card h-100" :class="{ 'current-package': value.isMyPackage }">
                                    <div class="card-body p-4">
                                        <!-- Current Package Badge -->
                                        <div v-if="value.isMyPackage" class="current-badge">
                                            <i class="fas fa-check-circle me-2"></i>
                                            Paket Saat Ini
                                        </div>
                                        
                                        <!-- Package Header -->
                                        <div class="package-header text-center mb-4">
                                            <h5 class="package-name fw-bold text-dark mb-2">{{ value.name }}</h5>
                                            <div class="package-price">
                                                <span class="price-amount fw-bold">{{ formatRupiah(value.price) }}</span>
                                                <span class="price-duration text-muted">{{ value.duration }} Hari</span>
                                            </div>
                                        </div>
                                        
                                        <!-- Package Features -->
                                        <div class="package-features-list">
                                            <div class="feature-item">
                                                <i class="fas fa-user text-primary me-2"></i>
                                                <span>{{ value.lisensi }} Lisensi</span>
                                            </div>
                                            <div class="feature-item" v-if="value.pesan != '0'">
                                                <i class="fas fa-comment text-success me-2"></i>
                                                <span>{{ value.pesan }} Pesan</span>
                                            </div>
                                            <div class="feature-item" v-if="isWebhookMediaAddon(value)">
                                                <i class="fas fa-hdd text-info me-2"></i>
                                                <span>{{ webhookMediaQuotaGB(value) }} GB storage media webhook (per device, terpisah dari paket utama)</span>
                                            </div>
                                            <div class="feature-item" v-if="value.device != '0'">
                                                <i class="fas fa-mobile-alt text-primary me-2"></i>
                                                <span>{{ value.device }} Device</span>
                                            </div>
                                            <div class="feature-item" v-if="value.customer_service != '0'">
                                                <i class="fas fa-headset text-info me-2"></i>
                                                <span>{{ value.customer_service }} Customer Service</span>
                                            </div>
                                            <div class="feature-item" v-if="value.campaign != '0'">
                                                <i class="fas fa-bullhorn text-warning me-2"></i>
                                                <span>{{ value.campaign }} Campaign</span>
                                            </div>
                                            <div class="feature-item" v-if="value.link_rotator != '0'">
                                                <i class="fas fa-link text-secondary me-2"></i>
                                                <span>{{ value.link_rotator }} Product Link Rotator</span>
                                            </div>
                                            <div class="feature-item" v-if="value.chat_widget != '0'">
                                                <i class="fas fa-comments text-success me-2"></i>
                                                <span>{{ value.chat_widget }} Chat Widget</span>
                                            </div>
                                            <div class="feature-item" v-if="value.chat_gpt != '0'">
                                                <i class="fas fa-robot text-purple me-2"></i>
                                                <span>{{ value.chat_gpt }} Chat GPT</span>
                                            </div>
                                            <div class="feature-item" v-if="value.daily_leads == 1">
                                                <i class="fas fa-chart-line text-info me-2"></i>
                                                <span>{{ value.daily_leads }} Daily Leads</span>
                                            </div>
                                            <div class="feature-item" v-if="value.group_greeter != '0'">
                                                <i class="fas fa-users text-primary me-2"></i>
                                                <span>{{ value.group_greeter }} Group Greeter</span>
                                            </div>
                                            <div class="feature-item" v-if="value.webhook_group == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Webhook Group</span>
                                            </div>
                                            <div class="feature-item" v-if="value.autoreply_group == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Autoreply Group</span>
                                            </div>
                                            <div class="feature-item" v-if="value.webhook_premium == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Webhook Premium</span>
                                            </div>
                                            <div class="feature-item" v-if="value.advance_contact == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Advanced Contact</span>
                                            </div>
                                            <div class="feature-item" v-if="value.birthday == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Birthday / Ulang Tahun</span>
                                            </div>
                                            <div class="feature-item" v-if="value.follow_up == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Follow Up Otomatis</span>
                                            </div>
                                        </div>
                                        
                                        <!-- Action Button -->
                                        <div class="package-action mt-4">
                                            <router-link :to="'/billing/buy/' + value.id" v-if="value.price != 0" class="w-100">
                                                <button :class="['btn w-100', value.isMyPackage ? 'btn-outline-primary' : 'btn-primary']">
                                                    <i :class="value.isMyPackage ? 'fas fa-sync-alt' : 'fas fa-shopping-cart'" class="me-2"></i>
                                                    {{ value.isMyPackage ? 'Perpanjang' : 'Beli Sekarang' }}
                                                </button>
                                            </router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Add-on Packages -->
                    <div class="mb-5">
                        <div class="section-header mb-4">
                            <h3 class="fw-bold text-dark mb-2">
                                <i class="fas fa-puzzle-piece text-info me-2"></i>
                                Paket Add-On
                            </h3>
                            <p class="text-muted mb-0">Tambahan fitur yang dapat melengkapi paket utama Anda</p>
                        </div>
                        
                        <div class="row">
                            <div v-for="(value, index) in packageListAddon" :key="'addon-' + index"
                                class="col-xxl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                <div class="package-card addon-card h-100" :class="{ 'current-package': value.isMyPackage }">
                                    <div class="card-body p-4">
                                        <!-- Current Package Badge -->
                                        <div v-if="value.isMyPackage" class="current-badge">
                                            <i class="fas fa-check-circle me-2"></i>
                                            Aktif
                                        </div>
                                        
                                        <!-- Add-on Badge -->
                                        <div class="addon-badge">
                                            <i class="fas fa-plus-circle me-2"></i>
                                            Add-On
                                        </div>
                                        
                                        <!-- Package Header -->
                                        <div class="package-header text-center mb-4">
                                            <h5 class="package-name fw-bold text-dark mb-2">{{ value.name }}</h5>
                                            <div class="package-price">
                                                <span class="price-amount fw-bold">{{ formatRupiah(value.price) }}</span>
                                                <span v-if="isWebhookMediaAddon(value)" class="price-duration text-muted d-block">Berlangganan per bulan (30 hari / device)</span>
                                                <span v-else class="price-duration text-muted d-block">Mengikuti masa aktif paket utama</span>
                                            </div>
                                        </div>
                                        
                                        <!-- Package Features -->
                                        <div class="package-features-list">
                                            <div class="feature-item">
                                                <i class="fas fa-user text-primary me-2"></i>
                                                <span>{{ value.lisensi }} Lisensi</span>
                                            </div>
                                            <div class="feature-item" v-if="value.pesan != '0'">
                                                <i class="fas fa-comment text-success me-2"></i>
                                                <span>{{ value.pesan }} Pesan</span>
                                            </div>
                                            <div class="feature-item" v-if="isWebhookMediaAddon(value)">
                                                <i class="fas fa-hdd text-info me-2"></i>
                                                <span>{{ webhookMediaQuotaGB(value) }} GB storage media webhook (per device, terpisah dari paket utama)</span>
                                            </div>
                                            <div class="feature-item" v-if="value.device != '0'">
                                                <i class="fas fa-mobile-alt text-primary me-2"></i>
                                                <span>{{ value.device }} Device</span>
                                            </div>
                                            <div class="feature-item" v-if="value.customer_service != '0'">
                                                <i class="fas fa-headset text-info me-2"></i>
                                                <span>{{ value.customer_service }} Customer Service</span>
                                            </div>
                                            <div class="feature-item" v-if="value.campaign != '0'">
                                                <i class="fas fa-bullhorn text-warning me-2"></i>
                                                <span>{{ value.campaign }} Campaign</span>
                                            </div>
                                            <div class="feature-item" v-if="value.link_rotator != '0'">
                                                <i class="fas fa-link text-secondary me-2"></i>
                                                <span>{{ value.link_rotator }} Product Link Rotator</span>
                                            </div>
                                            <div class="feature-item" v-if="value.chat_widget != '0'">
                                                <i class="fas fa-comments text-success me-2"></i>
                                                <span>{{ value.chat_widget }} Chat Widget</span>
                                            </div>
                                            <div class="feature-item" v-if="value.chat_gpt != '0'">
                                                <i class="fas fa-robot text-purple me-2"></i>
                                                <span>{{ value.chat_gpt }} Chat GPT</span>
                                            </div>
                                            <div class="feature-item" v-if="value.daily_leads == 1">
                                                <i class="fas fa-chart-line text-info me-2"></i>
                                                <span>{{ value.daily_leads }} Daily Leads</span>
                                            </div>
                                            <div class="feature-item" v-if="value.group_greeter != '0'">
                                                <i class="fas fa-users text-primary me-2"></i>
                                                <span>{{ value.group_greeter }} Group Greeter</span>
                                            </div>
                                            <div class="feature-item" v-if="value.webhook_group == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Webhook Group</span>
                                            </div>
                                            <div class="feature-item" v-if="value.autoreply_group == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Autoreply Group</span>
                                            </div>
                                            <div class="feature-item" v-if="value.webhook_premium == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Webhook Premium</span>
                                            </div>
                                            <div class="feature-item" v-if="value.advance_contact == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Advanced Contact</span>
                                            </div>
                                            <div class="feature-item" v-if="value.birthday == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Birthday / Ulang Tahun</span>
                                            </div>
                                            <div class="feature-item" v-if="value.follow_up == 1">
                                                <i class="fas fa-check-circle text-warning me-2"></i>
                                                <span>Follow Up Otomatis</span>
                                            </div>
                                        </div>
                                        
                                        <!-- Action Button -->
                                        <div class="package-action mt-4">
                                            <router-link :to="'/billing/buy/' + value.id" v-if="value.price != 0" class="w-100">
                                                <button :class="['btn w-100', value.isMyPackage ? 'btn-outline-primary' : 'btn-primary']">
                                                    <i :class="value.isMyPackage ? 'fas fa-sync-alt' : 'fas fa-shopping-cart'" class="me-2"></i>
                                                    {{ value.isMyPackage ? 'Perpanjang' : 'Beli Sekarang' }}
                                                </button>
                                            </router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modals -->
        <modal name="withdraw" :height="'auto'" :adaptive="true" :scrollable="true">
            <div style="margin: 20px">
                <Withdraw :withdrawableBalance="withdrawableBalance"></Withdraw>
            </div>
        </modal>
        <modal name="topup" :height="'auto'" :adaptive="true" :scrollable="true">
            <div style="margin: 20px">
                <TopUp></TopUp>
            </div>
        </modal>
        <modal name="sync" :height="'auto'" :adaptive="true" :scrollable="true">
            <div style="margin: 20px">
                {{ syncText }}
            </div>
        </modal>
    </div>
</template>

<script>
    import TopUp from "@/components/forms/billing/TopUp.vue";
    import Withdraw from "@/components/forms/billing/Withdraw.vue";
    import moment from 'moment'

    export default {
        components: {
            TopUp,
            Withdraw
        },
        data() {
            return {
                isLoading: true,
                myPackageLoading: true,
                myPackage: {
                    id: "",
                    package_id: "",
                    name: "",
                    pesan: "0",
                    device: "0",
                    customer_service: "0",
                    campaign: "0",
                    order_online: "0",
                    link_rotator: "0",
                    chat_widget: "0",
                    chat_gpt: "0",
                    daily_leads: "0",
                    group_greeter: "0",
                    webhook_group: "0",
                    autoreply_group: "0",
                    webhook_premium: "0",
                    advance_contact: "0",
                    birthday: "0",
                    follow_up: "0",
                    expired_at: "",
                    raw_expired_at: null, // Store raw timestamp for expiration check
                    webhook_media_quota:0

                },
                withdrawableBalance: 0,
                user_balance: 0,
                packageList: [],
                packageListAddon: [],
                packageListNotAddon: [],
                extendURL: "",
                syncText: "Sinkronisasi dalam proses..."
            };
        },
        computed: {
            affiliateOrderLink() {
                const u = this.$store.state.auth.user;
                const code = u && u.referral;
                if (!code) return "";
                return "https://app.starsender.online/order?ref=" + encodeURIComponent(code);
            },
        },
        beforeDestroy(){
            this.$root.$off("toggle_modal", this.fnToggleModal);
            this.$root.$off("refresh_data", this.fnRefreshData);
        },
        async mounted() {
            this.fnToggleModal = (param) => {
                this.toggleModal(param);
            }
            this.$root.$on("toggle_modal", this.fnToggleModal);

            this.fnRefreshData = (param) => {
                if(param == "user") this.fetchUser()
            }
            this.$root.$on("refresh_data", this.fnRefreshData);

            await this.fetchUser()
            await this.fetchPacakge()
            if(this.$store.state.auth.user.level == 'user-agency'){
                await this.fetchAgencySetting()
            }

            this.isLoading = false
            this.resumePendingBuy()
        },
        methods: {
            copyAffiliateOrderLink() {
                if (!this.affiliateOrderLink) return;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(this.affiliateOrderLink).then(() => {
                        this.$toast.success("Link afiliasi disalin");
                    }).catch(() => {
                        this.$toast.info(this.affiliateOrderLink);
                    });
                    return;
                }
                this.$toast.info(this.affiliateOrderLink);
            },
            async resumePendingBuy() {
                let raw = null;
                try { raw = localStorage.getItem("ss_pending_buy"); } catch (e) { return; }
                if (!raw) return;
                let intent;
                try { intent = JSON.parse(raw); } catch (e) { try { localStorage.removeItem("ss_pending_buy"); } catch (x) { /* noop */ } return; }
                if (!intent || !intent.package_id || !intent.amount) { try { localStorage.removeItem("ss_pending_buy"); } catch (x) { /* noop */ } return; }
                if (Date.now() - (intent.ts || 0) > 6 * 3600 * 1000) { try { localStorage.removeItem("ss_pending_buy"); } catch (x) { /* noop */ } return; }
                this.$toast.info("Memproses pembayaran & mengaktifkan paket Anda...");
                for (let i = 0; i < 40; i++) {
                    let bal = 0;
                    try { let u = await this.$store.dispatch("user_detail"); bal = parseInt(u.data.data.user.balance) || 0; } catch (e) { /* noop */ }
                    if (bal >= intent.amount) {
                        try { localStorage.removeItem("ss_pending_buy"); } catch (x) { /* noop */ }
                        try {
                            const payload = { id: parseInt(intent.package_id), code: intent.code || "" };
                            if (intent.id_device) payload.id_device = parseInt(intent.id_device);
                            await this.$store.dispatch("package_buy", payload);
                            this.$toast.success("Pembayaran berhasil — paket Anda sudah AKTIF!");
                            await this.fetchUser();
                        } catch (e) {
                            this.$toast.error("Saldo sudah masuk, tetapi aktivasi paket otomatis gagal. Silakan beli paket dari saldo (saldo Anda aman).");
                        }
                        return;
                    }
                    await new Promise(res => setTimeout(res, 3000));
                }
                this.$toast.warning("Pembayaran sedang diproses. Paket akan aktif otomatis setelah pembayaran dikonfirmasi — silakan refresh halaman ini sebentar lagi.");
            },
            async fetchUser() {
                try {
                    let user_data = await this.$store.dispatch('user_detail')
                    this.user_balance = user_data.data.data.user.balance
                    this.withdrawableBalance = user_data.data.data.user.withdrawable_balance
                    
                    // Update Vuex state with latest package data (this will auto-save to localStorage via vuex-persistedstate)
                    if (user_data?.data?.data?.package_data) {
                        this.$store.commit('auth/SET_PACKAGE', user_data.data.data.package_data)
                        const exp = user_data.data.data.package_data.expired_at
                        const expMs = typeof exp === 'number' ? exp : Number(exp)
                        if (Number.isFinite(expMs) && expMs >= Date.now()) {
                            try { sessionStorage.setItem('ss_package_fresh', '1') } catch (e) { /* noop */ }
                        }
                    }
                    
                    // Update user in Vuex state
                    if (user_data?.data?.data?.user) {
                        this.$store.commit('auth/SET_USER', user_data.data.data.user)
                    }
                    
                    this.fetchMyPacakge(user_data.data.data.package_data)
                } catch (err) {
                    this.$toast.error(err.response.data.message)
                    this.$router.push("/billing")
                }
            },
            async fetchMyPacakge(p){
                this.myPackageLoading = true
                try{
                    this.myPackage.id = p.id
                    this.myPackage.package_id = p.package_id
                    this.myPackage.name = p.package_data.name
                    this.myPackage.pesan = p.message_quota == "-1" ? "Unlimited" : p.message_quota
                    this.myPackage.device = p.max_device == "-1" ? "Unlimited" : p.max_device
                    this.myPackage.customer_service = p.max_cs == "-1" ? "Unlimited" : p.max_cs
                    this.myPackage.campaign = p.max_campaign == "-1" ? "Unlimited" : p.max_campaign
                    this.myPackage.order_online = p.max_order_online == "-1" ? "Unlimited" : p.max_order_online
                    this.myPackage.link_rotator = p.max_link_rotator == "-1" ? "Unlimited" : p.max_link_rotator
                    this.myPackage.chat_widget = p.max_chat_widget == "-1" ? "Unlimited" : p.max_chat_widget
                    this.myPackage.chat_gpt = p.max_chat_gpt == "-1" ? "Unlimited" : p.max_chat_gpt
                    this.myPackage.daily_leads = p.daily_leads == "-1" ? "Unlimited" : p.daily_leads
                    this.myPackage.group_greeter = p.group_greeter == "-1" ? "Unlimited" : p.group_greeter
                    this.myPackage.webhook_group = p.webhook_group
                    this.myPackage.autoreply_group = p.autoreply_group
                    this.myPackage.webhook_premium = p.webhook_premium
                    this.myPackage.advance_contact = p.advance_contact
                    this.myPackage.birthday = p.birthday
                    this.myPackage.follow_up = p.follow_up
                    this.myPackage.expired_at = moment.unix(p.expired_at / 1000).format('DD MMMM YYYY')
                    this.myPackage.webhook_media_quota = parseInt(p.webhook_media_quota)/1000000
                    // Store raw expired_at for isExpired() check
                    this.myPackage.raw_expired_at = p.expired_at
                }catch(err){
                    console.log(err)
                }
                this.myPackageLoading = false
            },
            async fetchPacakge(){
                this.myPackageLoading = true
                try{
                    let packageList = await this.$store.dispatch('package_list')
                    let pkg = packageList.data.data.packages
                    let p = []

                    for(let i = 0; i < pkg.length; i++){
                        if(this.$store.state.auth.user.level == 'user-agency' && pkg[i].is_addon == 0){
                            continue
                        }

                        p.push(pkg[i])
                    }

                    for (let i = 0; i < p.length; i++) {
                        p[i].pesan = p[i].message_quota == "-1" ? "Unlimited" : p[i].message_quota
                        p[i].device = p[i].max_device == "-1" ? "Unlimited" : p[i].max_device
                        p[i].customer_service = p[i].max_cs == "-1" ? "Unlimited" : p[i].max_cs
                        p[i].campaign = p[i].max_campaign == "-1" ? "Unlimited" : p[i].max_campaign
                        p[i].order_online = p[i].max_order_online == "-1" ? "Unlimited" : p[i].max_order_online
                        p[i].link_rotator = p[i].max_link_rotator == "-1" ? "Unlimited" : p[i].max_link_rotator
                        p[i].chat_widget = p[i].max_chat_widget == "-1" ? "Unlimited" : p[i].max_chat_widget
                        p[i].chat_gpt = p[i].max_chat_gpt == "-1" ? "Unlimited" : p[i].max_chat_gpt
                        p[i].group_greeter = p[i].group_greeter == "-1" ? "Unlimited" : p[i].group_greeter
                        p[i].webhook_media_quota = parseInt(p[i].webhook_media_quota)/1000000
                        if(p[i].id == this.myPackage.package_id){
                            p[i].isMyPackage = true
                        }else{
                            p[i].isMyPackage = false 
                        }
                    }

                    this.packageList = p    

                    //split to 2 array is_addon and not_addon and order by price from low to high add filter to hide package
                    this.packageListAddon = this.packageList.filter(item => item.is_addon == 1 && item.hide == 0 && item.price != 0).sort((a, b) => a.price - b.price)
                    this.packageListNotAddon = this.packageList.filter(item => item.is_addon == 0 && item.hide == 0 && item.price != 0).sort((a, b) => a.price - b.price)
                }catch(err){
                    console.log(err)
                }
                this.myPackageLoading = false
            },
            toggleModal(param) {
                this.$modal.toggle(param);
            },
            isWebhookMediaAddon(pkg) {
                if (!pkg) return false
                const q = Number(pkg.webhook_media_quota)
                if (q > 0) return true
                const n = (pkg.name || '').toLowerCase()
                return n.includes('webhook') && n.includes('media')
            },
            webhookMediaQuotaGB(pkg) {
                const q = Number(pkg.webhook_media_quota)
                if (q > 0) return q
                const m = (pkg.name || '').match(/(\d+)\s*gb/i)
                return m ? parseInt(m[1], 10) : 0
            },
            formatRupiah(numb){
                const format = numb.toString().split('').reverse().join('');
                const convert = format.match(/\d{1,3}/g);
                const rupiah = 'Rp ' + convert.join('.').split('').reverse().join('')
                return rupiah
            },
            matchPackage(a, b){
                if(a == b){
                    return true 
                }else{
                    return false
                }
            },
            isExpired(){
                // Use the most up-to-date data from myPackage (which is refreshed on mount)
                // Fallback to Vuex state if myPackage not yet loaded
                let expiredAt = null;
                
                if (this.myPackage && this.myPackage.raw_expired_at) {
                    // Use raw expired_at from latest fetch
                    expiredAt = this.myPackage.raw_expired_at;
                } else if (this.$store.state.auth.package && this.$store.state.auth.package.expired_at) {
                    // Fallback to Vuex state
                    expiredAt = this.$store.state.auth.package.expired_at;
                } else {
                    // No package data available, don't show expired
                    return false;
                }
                
                // Ensure expired_at is a number
                if (typeof expiredAt !== 'number') {
                    return false;
                }
                
                let now = moment.utc().valueOf();
                return expiredAt < now;
            },
            async fetchAgencySetting(){
                let setting = await this.$store.dispatch('agency_setting_user')
                this.extendURL = setting.data.data.setting.extend_url
            },
            async sync(){
                try{
                    const res = await this.$store.dispatch('package_sync', {})
                    const skipped = res && res.data && res.data.data && res.data.data.skipped
                    if (skipped) {
                        this.syncText = (res.data.message || "Sinkronisasi dilewati: data V3 lebih baru daripada Billing V2.") + " Paket Anda tidak diubah."
                    } else {
                        this.syncText = "Sinkronisasi berhasil, silakan refresh halaman ini"
                    }
                }catch(err){
                    this.syncText = "Sinkronisasi gagal, pastikan email di v2 dan v3 sama atau kontak admin"
                    this.$toast.error(err.response.data.message)
                }
            }
        },
    };
</script>

<style scoped>
/* General Styles */
/* .contents {
    background-color: #f5f5f5;
    min-height: 100vh;
    padding: 20px 0;
    margin-left: 0;
} */

/* Header Styles */
.breadcrumb-main {
    background: #ffffff;
    padding: 30px;
    border-radius: 8px;
    color: #333;
    margin-bottom: 30px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.breadcrumb-title {
    margin-bottom: 10px;
    font-size: 2.2rem;
    color: #333;
}

/* Alert Styles */
.alert-danger {
    border: none;
    border-radius: 8px;
    background: #f44336;
    color: white;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
}

/* Balance Card */
.balance-card {
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.balance-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.balance-icon {
    width: 60px;
    height: 60px;
    background: #e3f2fd;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1976d2;
}

.balance-card .btn {
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.balance-card .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* Current Package Card */
.current-package-card {
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.current-package-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.package-icon {
    width: 60px;
    height: 60px;
    background: #fff3e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #f57c00;
}

/* Package Features */
.package-features {
    margin-top: 20px;
}

.feature-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    font-size: 0.9rem;
    color: #666;
}

.feature-item.premium {
    color: #f57c00;
    font-weight: 600;
}

.feature-text {
    font-size: 0.85rem;
}

/* Package Expiry */
.package-expiry {
    border-top: 1px solid #e0e0e0;
    padding-top: 15px;
}

.expiry-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    color: #666;
}

.expiry-date {
    color: #d32f2f;
    font-weight: 600;
    margin-left: 5px;
}

/* Section Headers */
.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    position: relative;
    display: inline-block;
    color: #333;
}

.section-header h3::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #1976d2;
    border-radius: 2px;
}

/* Package Cards */
.package-card {
    background: white;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.package-card .card-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
}

.package-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
    border-color: #1976d2;
}

.package-card.current-package {
    border-color: #4caf50;
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.2);
}

/* .package-card.addon-card {
    border-left: 4px solid #00bcd4;
} */

/* Package Badges */
.current-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #4caf50;
    color: white;
    padding: 8px 15px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    z-index: 10;
}

.addon-badge {
    position: absolute;
    top: 15px;
    left: 15px;
    background: #00bcd4;
    color: white;
    padding: 6px 12px;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 600;
    z-index: 10;
}

/* Package Header */
.package-header {
    margin-top: 20px;
}

.package-name {
    font-size: 1.3rem;
    margin-bottom: 15px;
    color: #333;
}

.package-price {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
}

.price-amount {
    font-size: 1.8rem;
    color: #1976d2;
}

.price-duration {
    font-size: 0.9rem;
    color: #666;
}

/* Package Features List */
.package-features-list {
    margin: 20px 0;
    flex: 1;
}

.package-features-list .feature-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    font-size: 0.9rem;
    color: #666;
    border-bottom: 1px solid #f0f0f0;
}

.package-features-list .feature-item:last-child {
    border-bottom: none;
}

.package-features-list .feature-item i {
    width: 20px;
    text-align: center;
    margin-right: 10px;
}

/* Package Action */
.package-action {
    margin-top: auto;
    padding-top: 15px;
    position: relative;
    bottom: 0;
    width: 100%;
}

.package-action .btn {
    border-radius: 6px;
    font-weight: 500;
    padding: 8px 16px;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    text-decoration: none;
}

.package-action .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
    .contents {
        margin-left: 0;
        padding: 15px;
    }
    
    .breadcrumb-title {
        font-size: 1.8rem;
    }
    
    .balance-card,
    .current-package-card {
        margin-bottom: 20px;
    }
    
    .package-card {
        margin-bottom: 20px;
    }
    
    .section-header {
        text-align: left;
    }
    
    .section-header h3::after {
        left: 0;
        transform: none;
    }
}

@media (max-width: 576px) {
    .contents {
        padding: 10px;
        margin-left: 0;
    }
    
    .breadcrumb-main {
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .breadcrumb-title {
        font-size: 1.6rem;
    }
    
    .package-features-list .feature-item {
        font-size: 0.8rem;
    }
    
    .price-amount {
        font-size: 1.5rem;
    }
}

/* Loading Spinner */
.spinner-border {
    width: 3rem;
    height: 3rem;
}

/* Utility Classes */
.text-purple {
    color: #9c27b0 !important;
}

/* Hover Effects */
.card:hover {
    transition: all 0.3s ease;
}

.btn:hover {
    transition: all 0.3s ease;
}

/* Focus States */
.btn:focus {
    box-shadow: 0 0 0 0.2rem rgba(25, 118, 210, 0.25);
}

/* Animation Classes */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.package-card {
    animation: fadeInUp 0.4s ease-out;
}

.package-card:nth-child(1) { animation-delay: 0.1s; }
.package-card:nth-child(2) { animation-delay: 0.2s; }
.package-card:nth-child(3) { animation-delay: 0.3s; }
.package-card:nth-child(4) { animation-delay: 0.4s; }

/* Material Design Colors */
.btn-primary {
    background-color: #1976d2;
    border-color: #1976d2;
}

.btn-primary:hover {
    background-color: #1565c0;
    border-color: #1565c0;
}

.btn-outline-primary {
    color: #1976d2;
    border-color: #1976d2;
}

.btn-outline-primary:hover {
    background-color: #1976d2;
    border-color: #1976d2;
    color: white;
}

.btn-success {
    background-color: #4caf50;
    border-color: #4caf50;
}

.btn-success:hover {
    background-color: #45a049;
    border-color: #45a049;
}

.btn-outline-success {
    color: #4caf50;
    border-color: #4caf50;
}

.btn-outline-success:hover {
    background-color: #4caf50;
    border-color: #4caf50;
}

.btn-info {
    background-color: #00bcd4;
    border-color: #00bcd4;
}

.btn-info:hover {
    background-color: #00acc1;
    border-color: #00acc1;
}

.btn-outline-info {
    color: #00bcd4;
    border-color: #00bcd4;
}

.btn-outline-info:hover {
    background-color: #00bcd4;
    border-color: #00bcd4;
    color: white;
}

.btn-secondary {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-secondary:hover {
    background-color: #5a6268;
    border-color: #5a6268;
}

.btn-outline-secondary {
    color: #6c757d;
    border-color: #6c757d;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-dark {
    background-color: #343a40;
    border-color: #343a40;
}

.btn-dark:hover {
    background-color: #23272b;
    border-color: #23272b;
}

.btn-warning {
    background-color: #ff9800;
    border-color: #ff9800;
}

.btn-warning:hover {
    background-color: #e68900;
    border-color: #e68900;
}

/* Card shadows */
.card {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}
</style>
